// src/pages/AdminSoumissions.js
import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase.js";
import { useNavigate } from "react-router-dom";

function formatAvailabilityDate(dateKey) {
  try {
    const [y, m, d] = String(dateKey).split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateKey;
  }
}

function getAvailabilityPreview(disponibilitesCalendrier) {
  if (
    !disponibilitesCalendrier ||
    typeof disponibilitesCalendrier !== "object"
  ) {
    return [];
  }

  return Object.entries(disponibilitesCalendrier)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .slice(0, 3);
}

function countAvailabilities(disponibilitesCalendrier) {
  if (
    !disponibilitesCalendrier ||
    typeof disponibilitesCalendrier !== "object"
  ) {
    return 0;
  }

  return Object.keys(disponibilitesCalendrier).length;
}

function getUserFullName(user) {
  const prenom = user?.prenom || "";
  const nom = user?.nom || "";
  const full = `${prenom} ${nom}`.trim();
  return full || user?.displayName || user?.email || "Utilisateur";
}

function getUserPhone(user) {
  return (
    user?.telephone ||
    (user?.countryCode || user?.indicatif
      ? `${user?.countryCode || user?.indicatif} ${user?.phone || user?.numero || ""}`.trim()
      : "") ||
    "—"
  );
}

function getUserCity(user) {
  return user?.residence || user?.lieuResidence || user?.ville || "—";
}

function getUserProfileLabel(user) {
  return user?.niveau || user?.profession || user?.role || "Profil utilisateur";
}

function normalize(value) {
  return String(value || "").toLowerCase().trim();
}

function AdminSoumissions() {
  const [soumissions, setSoumissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeView, setActiveView] = useState("soumissions");

  const [roleFilter, setRoleFilter] = useState("tous");
  const [userFilter, setUserFilter] = useState("tous");
  const [availabilityFilter, setAvailabilityFilter] = useState("tous");

  const [currentPage, setCurrentPage] = useState(1);
  const [currentUsersPage, setCurrentUsersPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [userSortBy, setUserSortBy] = useState("name-asc");

  const pageSize = 10;

  const [openMessage, setOpenMessage] = useState(null);
  const [openAvailability, setOpenAvailability] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);

  const [theme, setTheme] = useState("light");

  const auth = getAuth();
  const navigate = useNavigate();
  const adminEmail = "modyhady94@gmail.com";

  const styles = getStyles(theme);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/connexion");
      return;
    }

    if (user.email !== adminEmail) {
      alert("⛔ Accès refusé. Cette page est réservée à l'administrateur.");
      navigate("/");
      return;
    }

    const fetchData = async () => {
      try {
        const q = query(
          collection(db, "soumissions"),
          orderBy("createdAT", "desc")
        );

        const [soumissionsSnapshot, usersSnapshot] = await Promise.all([
          getDocs(q),
          getDocs(collection(db, "users")),
        ]);

        const soumissionsData = soumissionsSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const usersData = usersSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const usersByUid = new Map();
        const usersByEmail = new Map();

        usersData.forEach((u) => {
          if (u.id) usersByUid.set(u.id, u);
          if (u.email) usersByEmail.set(normalize(u.email), u);
        });

        const enrichedSoumissions = soumissionsData.map((s) => {
          const linkedUser =
            (s.uid && usersByUid.get(s.uid)) ||
            (s.email && usersByEmail.get(normalize(s.email))) ||
            null;

          return {
            ...s,
            linkedUser,
            disponibilitesCalendrier: linkedUser?.disponibilitesCalendrier || {},
          };
        });

        const enrichedUsers = usersData.map((u) => {
          const relatedSoumissions = soumissionsData.filter(
            (s) =>
              (s.uid && s.uid === u.id) ||
              (s.email && normalize(s.email) === normalize(u.email))
          );

          return {
            ...u,
            relatedSoumissionsCount: relatedSoumissions.length,
            disponibilitesCalendrier: u?.disponibilitesCalendrier || {},
          };
        });

        setSoumissions(enrichedSoumissions);
        setUsers(enrichedUsers);
      } catch (error) {
        console.error("Erreur Firestore :", error);
        alert("Impossible de charger les données ❌");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth, navigate]);

  const goToDashboard = () => navigate("/dashboard");
  const goToHome = () => navigate("/education");

  const stats = useMemo(() => {
    const totalSoumissions = soumissions.length;
    const nbEleves = soumissions.filter((s) => s.role === "eleve").length;
    const nbTuteurs = soumissions.filter((s) => s.role === "tuteur").length;
    const nbEcoles = soumissions.filter((s) => s.role === "ecole").length;

    const totalUsers = users.length;
    const usersWithAvailabilities = users.filter(
      (u) => countAvailabilities(u.disponibilitesCalendrier) > 0
    ).length;

    return {
      totalSoumissions,
      nbEleves,
      nbTuteurs,
      nbEcoles,
      totalUsers,
      usersWithAvailabilities,
    };
  }, [soumissions, users]);

  const filteredSoumissions = useMemo(() => {
    let filtered = soumissions;

    if (roleFilter !== "tous") {
      filtered = filtered.filter((s) => s.role === roleFilter);
    }

    if (availabilityFilter === "avec-dispos") {
      filtered = filtered.filter(
        (s) => countAvailabilities(s.disponibilitesCalendrier) > 0
      );
    }

    if (availabilityFilter === "sans-dispos") {
      filtered = filtered.filter(
        (s) => countAvailabilities(s.disponibilitesCalendrier) === 0
      );
    }

    if (searchTerm.trim()) {
      const term = normalize(searchTerm);
      filtered = filtered.filter(
        (s) =>
          normalize(s.nom).includes(term) ||
          normalize(s.email).includes(term) ||
          normalize(s.ville).includes(term) ||
          normalize(s.telephone).includes(term)
      );
    }

    if (sortBy === "date-asc") {
      filtered = [...filtered].reverse();
    } else if (sortBy === "name-asc") {
      filtered = [...filtered].sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "")
      );
    } else if (sortBy === "name-desc") {
      filtered = [...filtered].sort((a, b) =>
        (b.nom || "").localeCompare(a.nom || "")
      );
    } else if (sortBy === "availabilities-desc") {
      filtered = [...filtered].sort(
        (a, b) =>
          countAvailabilities(b.disponibilitesCalendrier) -
          countAvailabilities(a.disponibilitesCalendrier)
      );
    }

    return filtered;
  }, [soumissions, roleFilter, searchTerm, sortBy, availabilityFilter]);

  const filteredUsers = useMemo(() => {
    let filtered = users;

    if (userFilter === "avec-dispos") {
      filtered = filtered.filter(
        (u) => countAvailabilities(u.disponibilitesCalendrier) > 0
      );
    }

    if (userFilter === "sans-dispos") {
      filtered = filtered.filter(
        (u) => countAvailabilities(u.disponibilitesCalendrier) === 0
      );
    }

    if (userFilter === "avec-soumission") {
      filtered = filtered.filter((u) => (u.relatedSoumissionsCount || 0) > 0);
    }

    if (userFilter === "sans-soumission") {
      filtered = filtered.filter((u) => (u.relatedSoumissionsCount || 0) === 0);
    }

    if (searchTerm.trim()) {
      const term = normalize(searchTerm);
      filtered = filtered.filter(
        (u) =>
          normalize(getUserFullName(u)).includes(term) ||
          normalize(u.email).includes(term) ||
          normalize(getUserCity(u)).includes(term) ||
          normalize(getUserPhone(u)).includes(term) ||
          normalize(getUserProfileLabel(u)).includes(term)
      );
    }

    if (userSortBy === "name-asc") {
      filtered = [...filtered].sort((a, b) =>
        getUserFullName(a).localeCompare(getUserFullName(b))
      );
    } else if (userSortBy === "name-desc") {
      filtered = [...filtered].sort((a, b) =>
        getUserFullName(b).localeCompare(getUserFullName(a))
      );
    } else if (userSortBy === "availabilities-desc") {
      filtered = [...filtered].sort(
        (a, b) =>
          countAvailabilities(b.disponibilitesCalendrier) -
          countAvailabilities(a.disponibilitesCalendrier)
      );
    } else if (userSortBy === "soumissions-desc") {
      filtered = [...filtered].sort(
        (a, b) => (b.relatedSoumissionsCount || 0) - (a.relatedSoumissionsCount || 0)
      );
    }

    return filtered;
  }, [users, searchTerm, userFilter, userSortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredSoumissions.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedSoumissions = filteredSoumissions.slice(
    startIndex,
    startIndex + pageSize
  );

  const totalUsersPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const usersStartIndex = (currentUsersPage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(
    usersStartIndex,
    usersStartIndex + pageSize
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const goToUsersPage = (page) => {
    if (page < 1 || page > totalUsersPages) return;
    setCurrentUsersPage(page);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("🗑️ Supprimer cette soumission ?")) return;

    try {
      await deleteDoc(doc(db, "soumissions", id));
      setSoumissions((prev) => prev.filter((s) => s.id !== id));

      setUsers((prev) =>
        prev.map((u) => {
          const relatedCount = soumissions.filter(
            (s) =>
              s.id !== id &&
              ((s.uid && s.uid === u.id) ||
                (s.email && normalize(s.email) === normalize(u.email)))
          ).length;

          return {
            ...u,
            relatedSoumissionsCount: relatedCount,
          };
        })
      );
    } catch (error) {
      console.error("Erreur suppression :", error);
      alert("Impossible de supprimer ❌");
    }
  };

  const handleDeleteUser = async (userItem) => {
    const userName = getUserFullName(userItem);

    const confirmDelete = window.confirm(
      `🗑️ Supprimer le profil de ${userName} ?\n\nCette action supprimera :\n- le document Firestore de l'utilisateur\n- la photo de profil si elle existe\n\n⚠️ Le compte Firebase Authentication ne sera pas supprimé ici.`
    );

    if (!confirmDelete) return;

    try {
      if (userItem?.photoURL) {
        try {
          const photoRef = ref(storage, userItem.photoURL);
          await deleteObject(photoRef);
        } catch (storageError) {
          console.warn("Photo non supprimée dans Storage :", storageError);
        }
      }

      await deleteDoc(doc(db, "users", userItem.id));

      setUsers((prev) => prev.filter((u) => u.id !== userItem.id));

      setSoumissions((prev) =>
        prev.map((s) => {
          const sameUid = s.uid && s.uid === userItem.id;
          const sameEmail =
            s.email &&
            userItem.email &&
            normalize(s.email) === normalize(userItem.email);

          if (sameUid || sameEmail) {
            return {
              ...s,
              linkedUser: null,
              disponibilitesCalendrier: {},
            };
          }

          return s;
        })
      );

      if (openAvailability?.email && normalize(openAvailability.email) === normalize(userItem.email)) {
        setOpenAvailability(null);
      }

      alert("✅ Profil utilisateur supprimé avec succès.");
    } catch (error) {
      console.error("Erreur suppression utilisateur :", error);
      alert("❌ Impossible de supprimer ce profil utilisateur.");
    }
  };

  const handleExportCSV = () => {
    if (activeView === "soumissions") {
      if (filteredSoumissions.length === 0) {
        alert("Aucune donnée à exporter.");
        return;
      }

      const headers = [
        "Nom",
        "Email",
        "Téléphone",
        "Rôle",
        "Ville",
        "Message",
        "Date",
        "Nb disponibilités",
      ];

      const escapeCSV = (value) => {
        const v = String(value ?? "");
        if (v.includes(";") || v.includes('"')) {
          return `"${v.replace(/"/g, '""')}"`;
        }
        return v;
      };

      const rows = filteredSoumissions.map((s) => [
        s.nom || "",
        s.email || "",
        s.telephone || "",
        s.role || "",
        s.ville || "",
        (s.message || "").replace(/\r?\n/g, " "),
        s.createdAT?.toDate ? s.createdAT.toDate().toLocaleString() : "",
        countAvailabilities(s.disponibilitesCalendrier),
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers, ...rows].map((r) => r.map(escapeCSV).join(";")).join("\n");

      const link = document.createElement("a");
      link.href = encodeURI(csvContent);
      link.download = "soumissions_hadycom_education.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (filteredUsers.length === 0) {
      alert("Aucun utilisateur à exporter.");
      return;
    }

    const headers = [
      "Nom complet",
      "Email",
      "Téléphone",
      "Niveau/Profession",
      "Ville",
      "Nb soumissions",
      "Nb disponibilités",
    ];

    const escapeCSV = (value) => {
      const v = String(value ?? "");
      if (v.includes(";") || v.includes('"')) {
        return `"${v.replace(/"/g, '""')}"`;
      }
      return v;
    };

    const rows = filteredUsers.map((u) => [
      getUserFullName(u),
      u.email || "",
      getUserPhone(u),
      getUserProfileLabel(u),
      getUserCity(u),
      u.relatedSoumissionsCount || 0,
      countAvailabilities(u.disponibilitesCalendrier),
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((r) => r.map(escapeCSV).join(";")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "users_hadycom_education.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinnerWrapper}>
          <div style={styles.spinner}></div>
          <div style={styles.spinnerInner}></div>
        </div>
        <p style={styles.loadingText}>Chargement des données...</p>
        <div style={styles.loadingDots} className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerGlow}></div>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.iconWrapper}>
              <span style={styles.icon}>📋</span>
              <div style={styles.iconGlow}></div>
            </div>
            <div>
              <h1 style={styles.title}>
                Administration <span style={styles.titleGradient}>HDC</span>
              </h1>
              <p style={styles.subtitle}>
                {activeView === "soumissions"
                  ? `${filteredSoumissions.length} soumission${filteredSoumissions.length > 1 ? "s" : ""}`
                  : `${filteredUsers.length} utilisateur${filteredUsers.length > 1 ? "s" : ""}`}{" "}
                • Vue moderne
              </p>
            </div>
          </div>

          <div style={styles.headerActions}>
            <button
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              style={styles.themeBtn}
              className="header-btn"
              type="button"
            >
              <span style={styles.btnIcon}>
                {theme === "light" ? "🌙" : "☀️"}
              </span>
              <span>{theme === "light" ? "Nuit" : "Jour"}</span>
            </button>

            <button onClick={goToHome} style={styles.homeBtn} className="header-btn">
              <span style={styles.btnIcon}>🏠</span>
              <span>Accueil</span>
            </button>

            <button
              onClick={goToDashboard}
              style={styles.dashboardBtn}
              className="header-btn"
            >
              <span style={styles.btnIcon}>📊</span>
              <span>Dashboard</span>
            </button>
          </div>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.statsBar}>
          {[
            {
              label: "Soumissions",
              value: stats.totalSoumissions,
              icon: "📨",
              gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              delay: 0,
            },
            {
              label: "Utilisateurs",
              value: stats.totalUsers,
              icon: "👥",
              gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              delay: 0.1,
            },
            {
              label: "Tuteurs",
              value: stats.nbTuteurs,
              icon: "👨‍🏫",
              gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              delay: 0.2,
            },
            {
              label: "Avec dispos",
              value: stats.usersWithAvailabilities,
              icon: "🗓️",
              gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              delay: 0.3,
            },
          ].map((stat, idx) => (
            <div
              key={idx}
              style={{ ...styles.statBox, animationDelay: `${stat.delay}s` }}
              className="stat-box"
            >
              <div style={{ ...styles.statIconWrapper, background: stat.gradient }}>
                <span style={styles.statIcon}>{stat.icon}</span>
              </div>
              <div style={styles.statContent}>
                <span style={styles.statLabel}>{stat.label}</span>
                <span style={styles.statValue}>{stat.value}</span>
              </div>
              <div style={styles.statTrend}>
                <span style={styles.trendArrow}>↗</span>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.viewTabsBar}>
          <button
            type="button"
            onClick={() => {
              setActiveView("soumissions");
              setCurrentPage(1);
            }}
            style={{
              ...styles.viewTabBtn,
              ...(activeView === "soumissions" ? styles.viewTabBtnActive : {}),
            }}
          >
            📨 Soumissions
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveView("users");
              setCurrentUsersPage(1);
            }}
            style={{
              ...styles.viewTabBtn,
              ...(activeView === "users" ? styles.viewTabBtnActive : {}),
            }}
          >
            👥 Utilisateurs inscrits
          </button>
        </div>

        <div style={styles.searchBar}>
          <div style={styles.searchGroup}>
            <div style={styles.searchInputWrapper}>
              <span style={styles.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder={
                  activeView === "soumissions"
                    ? "Rechercher par nom, email, ville..."
                    : "Rechercher un utilisateur par nom, email, ville..."
                }
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                  setCurrentUsersPage(1);
                }}
                style={styles.searchInput}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={styles.clearBtn}
                  className="clear-btn"
                  type="button"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div style={styles.filterControls}>
            {activeView === "soumissions" ? (
              <>
                <select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={styles.select}
                  className="filter-select"
                >
                  <option value="tous">👥 Tous les profils</option>
                  <option value="eleve">👨‍🎓 Élèves</option>
                  <option value="tuteur">👨‍🏫 Tuteurs</option>
                  <option value="ecole">🏫 Écoles</option>
                </select>

                <select
                  value={availabilityFilter}
                  onChange={(e) => {
                    setAvailabilityFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={styles.select}
                  className="filter-select"
                >
                  <option value="tous">🗓️ Toutes disponibilités</option>
                  <option value="avec-dispos">✅ Avec disponibilités</option>
                  <option value="sans-dispos">❌ Sans disponibilités</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={styles.select}
                  className="filter-select"
                >
                  <option value="date-desc">📅 Plus récent</option>
                  <option value="date-asc">📅 Plus ancien</option>
                  <option value="name-asc">🔤 Nom A-Z</option>
                  <option value="name-desc">🔤 Nom Z-A</option>
                  <option value="availabilities-desc">🗓️ Plus de dispos</option>
                </select>
              </>
            ) : (
              <>
                <select
                  value={userFilter}
                  onChange={(e) => {
                    setUserFilter(e.target.value);
                    setCurrentUsersPage(1);
                  }}
                  style={styles.select}
                  className="filter-select"
                >
                  <option value="tous">👥 Tous les inscrits</option>
                  <option value="avec-dispos">✅ Avec disponibilités</option>
                  <option value="sans-dispos">❌ Sans disponibilités</option>
                  <option value="avec-soumission">📨 Avec soumission</option>
                  <option value="sans-soumission">🆕 Sans soumission</option>
                </select>

                <select
                  value={userSortBy}
                  onChange={(e) => setUserSortBy(e.target.value)}
                  style={styles.select}
                  className="filter-select"
                >
                  <option value="name-asc">🔤 Nom A-Z</option>
                  <option value="name-desc">🔤 Nom Z-A</option>
                  <option value="availabilities-desc">🗓️ Plus de dispos</option>
                  <option value="soumissions-desc">📨 Plus de soumissions</option>
                </select>
              </>
            )}

            <button
              onClick={handleExportCSV}
              style={styles.exportBtn}
              className="export-btn"
            >
              <span style={styles.btnIcon}>📤</span>
              <span>Exporter CSV</span>
            </button>
          </div>
        </div>

        {activeView === "soumissions" ? (
          filteredSoumissions.length === 0 ? (
            <div style={styles.emptyState} className="empty-state">
              <div style={styles.emptyIcon}>🔍</div>
              <h3 style={styles.emptyTitle}>Aucun résultat</h3>
              <p style={styles.emptyText}>
                {searchTerm
                  ? `Aucune soumission ne correspond à "${searchTerm}"`
                  : "Aucune donnée ne correspond à votre filtre actuel."}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.resultsInfo}>
                <span style={styles.resultsText}>
                  Affichage de <strong>{startIndex + 1}</strong> à{" "}
                  <strong>
                    {Math.min(startIndex + pageSize, filteredSoumissions.length)}
                  </strong>{" "}
                  sur <strong>{filteredSoumissions.length}</strong> résultat
                  {filteredSoumissions.length > 1 ? "s" : ""}
                </span>
                {searchTerm && (
                  <span style={styles.searchBadge}>
                    🔍 Recherche active : "{searchTerm}"
                  </span>
                )}
              </div>

              <div style={styles.tableWrapper} className="table-wrapper">
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}><div style={styles.thContent}><span>👤</span><span>Nom</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>✉️</span><span>Email</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>📞</span><span>Téléphone</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>🎯</span><span>Rôle</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>📍</span><span>Ville</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>💬</span><span>Message</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>📅</span><span>Date</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>🗓️</span><span>Disponibilités</span></div></th>
                      <th style={styles.th}><div style={styles.thContent}><span>⚙️</span><span>Actions</span></div></th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedSoumissions.map((s, idx) => (
                      <tr
                        key={s.id}
                        style={{
                          ...styles.tr,
                          animationDelay: `${idx * 0.05}s`,
                          ...(hoveredRow === s.id ? styles.trHovered : {}),
                        }}
                        className="table-row"
                        onMouseEnter={() => setHoveredRow(s.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td style={styles.td}>
                          <div style={styles.nameCell}>
                            <div style={styles.avatar}>
                              {s.nom?.charAt(0).toUpperCase() || "?"}
                            </div>
                            <span style={styles.nameText}>{s.nom || "—"}</span>
                          </div>
                        </td>

                        <td style={styles.td}>
                          {s.email ? (
                            <a
                              href={`mailto:${s.email}`}
                              style={styles.emailLink}
                              className="email-link"
                            >
                              <span style={styles.emailIcon}>✉️</span>
                              {s.email}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={styles.td}>
                          {s.telephone ? (
                            <a
                              href={`tel:${s.telephone}`}
                              style={styles.phoneLink}
                              className="phone-link"
                            >
                              <span style={styles.phoneIcon}>📞</span>
                              {s.telephone}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.roleBadge,
                              ...(s.role === "eleve"
                                ? styles.roleEleve
                                : s.role === "tuteur"
                                ? styles.roleTuteur
                                : styles.roleEcole),
                            }}
                            className="role-badge"
                          >
                            {s.role === "eleve"
                              ? "👨‍🎓 Élève"
                              : s.role === "tuteur"
                              ? "👨‍🏫 Tuteur"
                              : "🏫 École"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.cityCell}>
                            <span style={styles.cityIcon}>📍</span>
                            <span>{s.ville || "—"}</span>
                          </div>
                        </td>

                        <td style={styles.tdMessage}>
                          <div style={styles.messageCell}>
                            <span style={styles.messagePreview}>
                              {s.message
                                ? s.message.substring(0, 50) +
                                  (s.message.length > 50 ? "..." : "")
                                : "—"}
                            </span>
                            {s.message && (
                              <button
                                type="button"
                                style={styles.viewBtn}
                                onClick={() => setOpenMessage(s)}
                                className="view-btn"
                              >
                                <span>👁️</span>
                                <span>Voir</span>
                              </button>
                            )}
                          </div>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.dateCell}>
                            <span style={styles.dateText}>
                              {s.createdAT?.toDate
                                ? new Date(s.createdAT.toDate()).toLocaleDateString(
                                    "fr-FR",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "—"}
                            </span>
                            <span style={styles.timeText}>
                              {s.createdAT?.toDate
                                ? new Date(s.createdAT.toDate()).toLocaleTimeString(
                                    "fr-FR",
                                    { hour: "2-digit", minute: "2-digit" }
                                  )
                                : ""}
                            </span>
                          </div>
                        </td>

                        <td style={styles.td}>
                          {(() => {
                            const preview = getAvailabilityPreview(
                              s.disponibilitesCalendrier
                            );

                            if (!preview.length) {
                              return (
                                <span style={styles.noAvailabilityText}>
                                  Aucune
                                </span>
                              );
                            }

                            return (
                              <div style={styles.availabilityCell}>
                                {preview.map(([dateKey, data]) => (
                                  <div key={dateKey} style={styles.availabilityMiniItem}>
                                    <span style={styles.availabilityMiniDate}>
                                      {formatAvailabilityDate(dateKey)}
                                    </span>
                                    <span style={styles.availabilityMiniSlot}>
                                      {data?.horaire || "—"}
                                    </span>
                                  </div>
                                ))}

                                <button
                                  type="button"
                                  style={styles.viewBtn}
                                  onClick={() => setOpenAvailability(s)}
                                  className="view-btn"
                                >
                                  <span>👁️</span>
                                  <span>Voir</span>
                                </button>
                              </div>
                            );
                          })()}
                        </td>

                        <td style={styles.td}>
                          <button
                            onClick={() => handleDelete(s.id)}
                            style={styles.deleteBtn}
                            className="delete-btn"
                          >
                            <span style={styles.deleteBtnIcon}>🗑️</span>
                            <span>Supprimer</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                goToPage={goToPage}
                styles={styles}
              />
            </>
          )
        ) : filteredUsers.length === 0 ? (
          <div style={styles.emptyState} className="empty-state">
            <div style={styles.emptyIcon}>👥</div>
            <h3 style={styles.emptyTitle}>Aucun utilisateur trouvé</h3>
            <p style={styles.emptyText}>
              Aucun utilisateur ne correspond aux filtres actuels.
            </p>
          </div>
        ) : (
          <>
            <div style={styles.resultsInfo}>
              <span style={styles.resultsText}>
                Affichage de <strong>{usersStartIndex + 1}</strong> à{" "}
                <strong>
                  {Math.min(usersStartIndex + pageSize, filteredUsers.length)}
                </strong>{" "}
                sur <strong>{filteredUsers.length}</strong> utilisateur
                {filteredUsers.length > 1 ? "s" : ""}
              </span>
              {searchTerm && (
                <span style={styles.searchBadge}>
                  🔍 Recherche active : "{searchTerm}"
                </span>
              )}
            </div>

            <div style={styles.tableWrapper} className="table-wrapper">
              <table style={styles.usersTable}>
                <thead>
                  <tr>
                    <th style={styles.th}><div style={styles.thContent}><span>👤</span><span>Utilisateur</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>✉️</span><span>Email</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>📞</span><span>Téléphone</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>🎓</span><span>Profil</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>📍</span><span>Ville</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>📨</span><span>Soumissions</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>🗓️</span><span>Disponibilités</span></div></th>
                    <th style={styles.th}><div style={styles.thContent}><span>⚙️</span><span>Actions</span></div></th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedUsers.map((u, idx) => {
                    const preview = getAvailabilityPreview(u.disponibilitesCalendrier);

                    return (
                      <tr
                        key={u.id}
                        style={{
                          ...styles.tr,
                          animationDelay: `${idx * 0.05}s`,
                          ...(hoveredRow === u.id ? styles.trHovered : {}),
                        }}
                        className="table-row"
                        onMouseEnter={() => setHoveredRow(u.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td style={styles.td}>
                          <div style={styles.nameCell}>
                            <div style={styles.avatar}>
                              {getUserFullName(u).charAt(0).toUpperCase()}
                            </div>
                            <div style={styles.userIdentityBlock}>
                              <span style={styles.nameText}>{getUserFullName(u)}</span>
                              <span style={styles.userIdText}>ID: {u.id}</span>
                            </div>
                          </div>
                        </td>

                        <td style={styles.td}>
                          {u.email ? (
                            <a
                              href={`mailto:${u.email}`}
                              style={styles.emailLink}
                              className="email-link"
                            >
                              <span style={styles.emailIcon}>✉️</span>
                              {u.email}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={styles.td}>
                          {getUserPhone(u) !== "—" ? (
                            <a
                              href={`tel:${getUserPhone(u)}`}
                              style={styles.phoneLink}
                              className="phone-link"
                            >
                              <span style={styles.phoneIcon}>📞</span>
                              {getUserPhone(u)}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={styles.td}>
                          <span style={styles.userProfileBadge}>
                            {getUserProfileLabel(u)}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.cityCell}>
                            <span style={styles.cityIcon}>📍</span>
                            <span>{getUserCity(u)}</span>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <span style={styles.countBadge}>
                            {u.relatedSoumissionsCount || 0}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {preview.length > 0 ? (
                            <div style={styles.availabilityCell}>
                              {preview.map(([dateKey, data]) => (
                                <div key={dateKey} style={styles.availabilityMiniItem}>
                                  <span style={styles.availabilityMiniDate}>
                                    {formatAvailabilityDate(dateKey)}
                                  </span>
                                  <span style={styles.availabilityMiniSlot}>
                                    {data?.horaire || "—"}
                                  </span>
                                </div>
                              ))}
                              <button
                                type="button"
                                style={styles.viewBtn}
                                onClick={() =>
                                  setOpenAvailability({
                                    nom: getUserFullName(u),
                                    email: u.email,
                                    ville: getUserCity(u),
                                    disponibilitesCalendrier: u.disponibilitesCalendrier,
                                  })
                                }
                                className="view-btn"
                              >
                                <span>👁️</span>
                                <span>Voir</span>
                              </button>
                            </div>
                          ) : (
                            <span style={styles.noAvailabilityText}>Aucune</span>
                          )}
                        </td>

                        <td style={styles.td}>
                          <button
                            onClick={() => handleDeleteUser(u)}
                            style={styles.deleteBtn}
                            className="delete-btn"
                          >
                            <span style={styles.deleteBtnIcon}>🗑️</span>
                            <span>Supprimer</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentUsersPage}
              totalPages={totalUsersPages}
              goToPage={goToUsersPage}
              styles={styles}
            />
          </>
        )}
      </div>

      {openMessage && (
        <div
          style={styles.modalOverlay}
          onClick={() => setOpenMessage(null)}
          className="modal-overlay"
        >
          <div
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
            className="modal-box"
          >
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                <div style={styles.modalIcon}>💬</div>
                <div>
                  <h3 style={styles.modalTitle}>Message complet</h3>
                  <p style={styles.modalSub}>
                    {openMessage.nom || "—"} • {openMessage.email || "—"}
                  </p>
                </div>
              </div>

              <button
                type="button"
                style={styles.modalClose}
                onClick={() => setOpenMessage(null)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div style={styles.modalMeta}>
              <span style={styles.metaPill}>
                {openMessage.role === "eleve"
                  ? "👨‍🎓 Élève"
                  : openMessage.role === "tuteur"
                  ? "👨‍🏫 Tuteur"
                  : "🏫 École"}
              </span>
              <span style={styles.metaPill}>📍 {openMessage.ville || "—"}</span>
              {openMessage.telephone && (
                <a
                  href={`tel:${openMessage.telephone}`}
                  style={styles.metaLink}
                  className="meta-link"
                >
                  📞 {openMessage.telephone}
                </a>
              )}
              {openMessage.createdAT?.toDate && (
                <span style={styles.metaPill}>
                  📅{" "}
                  {new Date(openMessage.createdAT.toDate()).toLocaleDateString(
                    "fr-FR",
                    { day: "numeric", month: "long", year: "numeric" }
                  )}
                </span>
              )}
            </div>

            <div style={styles.modalBody}>
              <p style={styles.modalMessage}>
                {openMessage.message || "Aucun message"}
              </p>
            </div>

            <div style={styles.modalActions}>
              {openMessage.email && (
                <a
                  href={`mailto:${openMessage.email}`}
                  style={styles.modalBtnPrimary}
                  className="modal-btn-primary"
                >
                  <span>✉️</span>
                  <span>Répondre par email</span>
                </a>
              )}
              <button
                type="button"
                style={styles.modalBtn}
                onClick={() => setOpenMessage(null)}
                className="modal-btn"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {openAvailability && (
        <div
          style={styles.modalOverlay}
          onClick={() => setOpenAvailability(null)}
          className="modal-overlay"
        >
          <div
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
            className="modal-box"
          >
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                <div style={styles.modalIcon}>🗓️</div>
                <div>
                  <h3 style={styles.modalTitle}>Disponibilités du profil</h3>
                  <p style={styles.modalSub}>
                    {openAvailability.nom || "—"} • {openAvailability.email || "—"}
                  </p>
                </div>
              </div>

              <button
                type="button"
                style={styles.modalClose}
                onClick={() => setOpenAvailability(null)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div style={styles.modalMeta}>
              <span style={styles.metaPill}>
                Total : {Object.keys(openAvailability.disponibilitesCalendrier || {}).length}
              </span>
              <span style={styles.metaPill}>
                📍 {openAvailability.ville || "—"}
              </span>
            </div>

            <div style={styles.modalBody}>
              {Object.keys(openAvailability.disponibilitesCalendrier || {}).length > 0 ? (
                <div style={styles.adminAvailabilityList}>
                  {Object.entries(openAvailability.disponibilitesCalendrier)
                    .sort(([a], [b]) => new Date(a) - new Date(b))
                    .map(([dateKey, data]) => (
                      <div key={dateKey} style={styles.adminAvailabilityItem}>
                        <div style={styles.adminAvailabilityDate}>
                          {formatAvailabilityDate(dateKey)}
                        </div>
                        <div style={styles.adminAvailabilitySlot}>
                          {data?.horaire || "Horaire non défini"}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p style={styles.modalMessage}>
                  Aucune disponibilité renseignée pour ce profil.
                </p>
              )}
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.modalBtn}
                onClick={() => setOpenAvailability(null)}
                className="modal-btn"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ currentPage, totalPages, goToPage, styles }) {
  return (
    <div style={styles.pagination}>
      <button
        onClick={() => goToPage(1)}
        disabled={currentPage === 1}
        style={{
          ...styles.pageBtn,
          ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
        }}
        className="page-btn"
      >
        <span>««</span>
      </button>

      <button
        onClick={() => goToPage(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          ...styles.pageBtn,
          ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
        }}
        className="page-btn"
      >
        <span style={styles.pageArrow}>←</span>
        <span>Précédent</span>
      </button>

      <div style={styles.pageNumbers}>
        {[...Array(Math.min(5, totalPages))].map((_, i) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }

          return (
            <button
              key={i}
              onClick={() => goToPage(pageNum)}
              style={{
                ...styles.pageNumBtn,
                ...(currentPage === pageNum ? styles.pageNumBtnActive : {}),
              }}
              className="page-num-btn"
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          ...styles.pageBtn,
          ...(currentPage === totalPages ? styles.pageBtnDisabled : {}),
        }}
        className="page-btn"
      >
        <span>Suivant</span>
        <span style={styles.pageArrow}>→</span>
      </button>

      <button
        onClick={() => goToPage(totalPages)}
        disabled={currentPage === totalPages}
        style={{
          ...styles.pageBtn,
          ...(currentPage === totalPages ? styles.pageBtnDisabled : {}),
        }}
        className="page-btn"
      >
        <span>»»</span>
      </button>
    </div>
  );
}

function getStyles(theme) {
  const isDark = theme === "dark";

  return {
    container: {
      minHeight: "100vh",
      background: isDark
        ? "linear-gradient(135deg, #0b1220 0%, #111827 100%)"
        : "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: "all 0.3s ease",
    },

    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    },
    spinnerWrapper: {
      position: "relative",
      width: "70px",
      height: "70px",
    },
    spinner: {
      position: "absolute",
      width: "70px",
      height: "70px",
      border: "4px solid rgba(255, 255, 255, 0.2)",
      borderTop: "4px solid #fff",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    spinnerInner: {
      position: "absolute",
      width: "50px",
      height: "50px",
      top: "10px",
      left: "10px",
      border: "4px solid rgba(255, 255, 255, 0.2)",
      borderTop: "4px solid rgba(255, 255, 255, 0.6)",
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite reverse",
    },
    loadingText: {
      marginTop: "24px",
      color: "#fff",
      fontSize: "16px",
      fontWeight: "700",
    },
    loadingDots: {
      display: "flex",
      gap: "8px",
      marginTop: "12px",
    },

    header: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: "24px 5%",
      boxShadow: "0 12px 48px rgba(102, 126, 234, 0.25)",
      position: "relative",
      overflow: "hidden",
    },
    headerGlow: {
      position: "absolute",
      top: "-50%",
      right: "-10%",
      width: "600px",
      height: "600px",
      background:
        "radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)",
      borderRadius: "50%",
      animation: "float 20s ease-in-out infinite",
    },
    headerContent: {
      maxWidth: "1400px",
      margin: "0 auto",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "18px",
      position: "relative",
      zIndex: 1,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
    },
    iconWrapper: {
      position: "relative",
      width: "56px",
      height: "56px",
      background: "rgba(255, 255, 255, 0.15)",
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
    iconGlow: {
      position: "absolute",
      inset: "-10px",
      background:
        "radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent 70%)",
      filter: "blur(12px)",
      animation: "pulse 3s ease-in-out infinite",
    },
    icon: {
      fontSize: "26px",
      position: "relative",
      zIndex: 1,
    },
    title: {
      color: "#fff",
      fontSize: "clamp(24px, 3.5vw, 32px)",
      fontWeight: "900",
      margin: "0 0 4px 0",
      letterSpacing: "-0.02em",
    },
    titleGradient: {
      background: "rgba(255, 255, 255, 0.9)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontWeight: "900",
    },
    subtitle: {
      color: "rgba(255, 255, 255, 0.9)",
      fontSize: "13px",
      margin: 0,
      fontWeight: "600",
    },
    headerActions: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap",
    },
    themeBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      background: "rgba(255, 255, 255, 0.15)",
      border: "1px solid rgba(255, 255, 255, 0.25)",
      borderRadius: "14px",
      color: "#fff",
      fontWeight: "800",
      fontSize: "13px",
      cursor: "pointer",
      backdropFilter: "blur(10px)",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    homeBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      background: "rgba(255, 255, 255, 0.15)",
      border: "1px solid rgba(255, 255, 255, 0.25)",
      borderRadius: "14px",
      color: "#fff",
      fontWeight: "800",
      fontSize: "13px",
      cursor: "pointer",
      backdropFilter: "blur(10px)",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    dashboardBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      background: "#fff",
      border: "none",
      borderRadius: "14px",
      color: "#667eea",
      fontWeight: "800",
      fontSize: "13px",
      cursor: "pointer",
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.15)",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    btnIcon: {
      fontSize: "18px",
    },

    content: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "28px 5% 54px",
    },

    statsBar: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "14px",
      marginBottom: "20px",
    },
    statBox: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      background: isDark ? "#111827" : "#fff",
      borderRadius: "16px",
      padding: "16px",
      boxShadow: isDark
        ? "0 6px 24px rgba(0, 0, 0, 0.35)"
        : "0 6px 24px rgba(2, 12, 27, 0.06)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
      position: "relative",
      overflow: "hidden",
      animation: "slideInUp 0.6s ease forwards",
      opacity: 0,
    },
    statIconWrapper: {
      width: "50px",
      height: "50px",
      borderRadius: "14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
      position: "relative",
    },
    statIcon: {
      fontSize: "26px",
    },
    statContent: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
    },
    statLabel: {
      fontSize: "11px",
      color: isDark ? "#cbd5e1" : "#586274",
      fontWeight: "800",
      marginBottom: "4px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    statValue: {
      fontSize: "26px",
      fontWeight: "900",
      color: isDark ? "#f8fafc" : "#0e1829",
      letterSpacing: "-0.02em",
    },
    statTrend: {
      position: "absolute",
      top: "14px",
      right: "14px",
      opacity: 0.3,
    },
    trendArrow: {
      fontSize: "22px",
      color: isDark ? "#fff" : "#0e1829",
    },

    viewTabsBar: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap",
      marginBottom: "18px",
    },
    viewTabBtn: {
      border: "1px solid rgba(102, 126, 234, 0.18)",
      background: isDark ? "#111827" : "#ffffff",
      color: isDark ? "#e2e8f0" : "#334155",
      padding: "12px 16px",
      borderRadius: "14px",
      fontWeight: "900",
      fontSize: "13px",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    viewTabBtnActive: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      border: "1px solid transparent",
      boxShadow: "0 8px 20px rgba(102, 126, 234, 0.25)",
    },

    searchBar: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginBottom: "18px",
      background: isDark ? "#111827" : "#fff",
      padding: "14px",
      borderRadius: "16px",
      boxShadow: isDark
        ? "0 6px 24px rgba(0, 0, 0, 0.35)"
        : "0 6px 24px rgba(2, 12, 27, 0.06)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
    },
    searchGroup: {
      width: "100%",
    },
    searchInputWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      width: "100%",
    },
    searchIcon: {
      position: "absolute",
      left: "14px",
      fontSize: "17px",
      pointerEvents: "none",
    },
    searchInput: {
      width: "100%",
      padding: "12px 44px 12px 44px",
      borderRadius: "14px",
      border: isDark
        ? "2px solid rgba(255,255,255,0.08)"
        : "2px solid rgba(102, 126, 234, 0.15)",
      fontSize: "14px",
      fontWeight: "600",
      color: isDark ? "#f8fafc" : "#0e1829",
      background: isDark
        ? "rgba(255,255,255,0.04)"
        : "rgba(102, 126, 234, 0.02)",
      transition: "all 0.3s ease",
      outline: "none",
    },
    clearBtn: {
      position: "absolute",
      right: "10px",
      width: "26px",
      height: "26px",
      borderRadius: "50%",
      border: "none",
      background: "rgba(102, 126, 234, 0.1)",
      color: "#667eea",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "800",
      transition: "all 0.3s ease",
    },
    filterControls: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      alignItems: "center",
    },
    select: {
      padding: "10px 14px",
      borderRadius: "14px",
      border: isDark
        ? "2px solid rgba(255,255,255,0.08)"
        : "2px solid rgba(102, 126, 234, 0.15)",
      fontSize: "13px",
      fontWeight: "800",
      color: isDark ? "#f8fafc" : "#0e1829",
      background: isDark ? "#0f172a" : "#fff",
      cursor: "pointer",
      transition: "all 0.3s ease",
      outline: "none",
    },
    exportBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      color: "#fff",
      border: "none",
      padding: "10px 18px",
      borderRadius: "14px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "13px",
      boxShadow: "0 8px 24px rgba(67, 233, 123, 0.3)",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },

    resultsInfo: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
      marginBottom: "14px",
      padding: "10px 14px",
      background: isDark
        ? "rgba(255,255,255,0.04)"
        : "rgba(102, 126, 234, 0.05)",
      borderRadius: "14px",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(102, 126, 234, 0.1)",
    },
    resultsText: {
      fontSize: "13px",
      color: isDark ? "#cbd5e1" : "#586274",
      fontWeight: "700",
    },
    searchBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "999px",
      background: "rgba(102, 126, 234, 0.1)",
      color: "#667eea",
      fontSize: "12px",
      fontWeight: "800",
    },

    emptyState: {
      textAlign: "center",
      padding: "80px 20px",
      background: isDark ? "#111827" : "#fff",
      borderRadius: "18px",
      boxShadow: isDark
        ? "0 6px 24px rgba(0, 0, 0, 0.35)"
        : "0 6px 24px rgba(2, 12, 27, 0.06)",
      animation: "fadeIn 0.5s ease",
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
    },
    emptyIcon: {
      fontSize: "72px",
      marginBottom: "20px",
    },
    emptyTitle: {
      fontSize: "24px",
      fontWeight: "900",
      color: isDark ? "#f8fafc" : "#0e1829",
      marginBottom: "12px",
    },
    emptyText: {
      fontSize: "15px",
      color: isDark ? "#cbd5e1" : "#586274",
      marginBottom: "24px",
    },
    emptyBtn: {
      padding: "12px 24px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      border: "none",
      borderRadius: "14px",
      fontWeight: "800",
      fontSize: "14px",
      cursor: "pointer",
      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
    },

    tableWrapper: {
      background: isDark ? "#111827" : "#fff",
      borderRadius: "18px",
      boxShadow: isDark
        ? "0 6px 24px rgba(0, 0, 0, 0.35)"
        : "0 6px 24px rgba(2, 12, 27, 0.06)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
      maxHeight: "65vh",
      overflowY: "auto",
      overflowX: "auto",
    },
    table: {
      width: "100%",
      minWidth: "1450px",
      borderCollapse: "collapse",
    },
    usersTable: {
      width: "100%",
      minWidth: "1500px",
      borderCollapse: "collapse",
    },
    th: {
      position: "sticky",
      top: 0,
      zIndex: 2,
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      padding: "16px",
      fontSize: "12px",
      fontWeight: "900",
      textAlign: "left",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    thContent: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    tr: {
      animation: "fadeInUp 0.5s ease forwards",
      opacity: 0,
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(230, 236, 255, 0.6)",
      transition: "all 0.3s ease",
    },
    trHovered: {
      background: isDark
        ? "rgba(255,255,255,0.04)"
        : "rgba(102, 126, 234, 0.04)",
      transform: "scale(1.002)",
    },
    td: {
      padding: "14px 16px",
      fontSize: "13px",
      color: isDark ? "#f8fafc" : "#0e1829",
      fontWeight: "700",
      verticalAlign: "middle",
    },
    tdMessage: {
      padding: "14px 16px",
      maxWidth: "320px",
    },
    messageCell: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    messagePreview: {
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: isDark ? "#cbd5e1" : "#586274",
      fontSize: "13px",
    },
    viewBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      border: "1px solid rgba(102, 126, 234, 0.2)",
      background: "rgba(102, 126, 234, 0.08)",
      color: "#667eea",
      padding: "8px 12px",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "900",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
    },

    nameCell: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    userIdentityBlock: {
      display: "grid",
      gap: "4px",
    },
    userIdText: {
      fontSize: "11px",
      color: isDark ? "#94a3b8" : "#64748b",
      fontWeight: "700",
    },
    avatar: {
      width: "38px",
      height: "38px",
      borderRadius: "14px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "900",
      fontSize: "14px",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
    nameText: {
      fontWeight: "900",
      color: isDark ? "#f8fafc" : "#0e1829",
    },
    emailLink: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "#667eea",
      textDecoration: "none",
      fontWeight: "800",
      transition: "all 0.3s ease",
    },
    emailIcon: {
      fontSize: "16px",
    },
    phoneLink: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: isDark ? "#f8fafc" : "#0e1829",
      textDecoration: "none",
      fontWeight: "800",
      transition: "all 0.3s ease",
    },
    phoneIcon: {
      fontSize: "16px",
    },
    cityCell: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    cityIcon: {
      fontSize: "16px",
    },
    dateCell: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    dateText: {
      fontSize: "13px",
      fontWeight: "800",
      color: isDark ? "#f8fafc" : "#0e1829",
    },
    timeText: {
      fontSize: "11px",
      color: isDark ? "#cbd5e1" : "#586274",
      fontWeight: "700",
    },

    availabilityCell: {
      display: "grid",
      gap: "8px",
      minWidth: "230px",
    },
    availabilityMiniItem: {
      display: "flex",
      justifyContent: "space-between",
      gap: "8px",
      alignItems: "center",
      padding: "8px 10px",
      borderRadius: "10px",
      background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #e8eef6",
    },
    availabilityMiniDate: {
      fontSize: "12px",
      fontWeight: "800",
      color: isDark ? "#f8fafc" : "#0e1829",
    },
    availabilityMiniSlot: {
      fontSize: "11px",
      fontWeight: "800",
      color: "#667eea",
      background: isDark ? "rgba(102,126,234,0.12)" : "#eef2ff",
      padding: "4px 8px",
      borderRadius: "999px",
      whiteSpace: "nowrap",
    },
    noAvailabilityText: {
      fontSize: "12px",
      fontWeight: "800",
      color: isDark ? "#94a3b8" : "#64748b",
      fontStyle: "italic",
    },

    adminAvailabilityList: {
      display: "grid",
      gap: "12px",
    },
    adminAvailabilityItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
      padding: "14px 16px",
      borderRadius: "14px",
      background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #e8eef6",
    },
    adminAvailabilityDate: {
      fontSize: "14px",
      fontWeight: "900",
      color: isDark ? "#f8fafc" : "#0e1829",
    },
    adminAvailabilitySlot: {
      fontSize: "12px",
      fontWeight: "900",
      color: "#667eea",
      background: isDark ? "rgba(102,126,234,0.12)" : "#eef2ff",
      padding: "8px 12px",
      borderRadius: "999px",
      whiteSpace: "nowrap",
    },

    roleBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 12px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "900",
      whiteSpace: "nowrap",
      transition: "all 0.3s ease",
    },
    roleEleve: {
      background: "rgba(102, 126, 234, 0.1)",
      color: "#667eea",
      border: "1.5px solid rgba(102, 126, 234, 0.2)",
    },
    roleTuteur: {
      background: "rgba(240, 147, 251, 0.1)",
      color: "#f093fb",
      border: "1.5px solid rgba(240, 147, 251, 0.2)",
    },
    roleEcole: {
      background: "rgba(79, 172, 254, 0.1)",
      color: "#4facfe",
      border: "1.5px solid rgba(79, 172, 254, 0.2)",
    },
    userProfileBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 12px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "900",
      background: isDark ? "rgba(255,255,255,0.06)" : "#f8fafc",
      color: isDark ? "#f8fafc" : "#0e1829",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid #e8eef6",
    },
    countBadge: {
      display: "inline-flex",
      minWidth: "34px",
      height: "34px",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "999px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      fontSize: "12px",
      fontWeight: "900",
      boxShadow: "0 6px 16px rgba(102, 126, 234, 0.25)",
    },

    deleteBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)",
      color: "#fff",
      border: "none",
      padding: "10px 14px",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "900",
      boxShadow: "0 6px 20px rgba(255, 107, 107, 0.3)",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      whiteSpace: "nowrap",
    },
    deleteBtnIcon: {
      fontSize: "16px",
    },

    pagination: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "8px",
      marginTop: "22px",
      padding: "18px",
      background: isDark ? "#111827" : "#fff",
      borderRadius: "18px",
      boxShadow: isDark
        ? "0 6px 24px rgba(0, 0, 0, 0.35)"
        : "0 6px 24px rgba(2, 12, 27, 0.06)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
      flexWrap: "wrap",
    },
    pageBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      border: "none",
      padding: "10px 16px",
      borderRadius: "14px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "13px",
      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.3)",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    pageBtnDisabled: {
      background: "rgba(88, 98, 116, 0.1)",
      color: isDark ? "#94a3b8" : "#586274",
      cursor: "not-allowed",
      boxShadow: "none",
      opacity: 0.5,
    },
    pageNumbers: {
      display: "flex",
      gap: "6px",
    },
    pageNumBtn: {
      width: "40px",
      height: "40px",
      border: isDark
        ? "2px solid rgba(255,255,255,0.08)"
        : "2px solid rgba(102, 126, 234, 0.15)",
      background: isDark ? "#0f172a" : "#fff",
      color: isDark ? "#f8fafc" : "#0e1829",
      borderRadius: "12px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "13px",
      transition: "all 0.3s ease",
    },
    pageNumBtnActive: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      border: "none",
      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.3)",
    },
    pageArrow: {
      fontSize: "16px",
    },

    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(2, 6, 23, 0.7)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "20px",
      animation: "fadeIn 0.3s ease",
    },
    modalBox: {
      width: "100%",
      maxWidth: "750px",
      background: isDark ? "rgba(17, 24, 39, 0.98)" : "rgba(255, 255, 255, 0.98)",
      borderRadius: "24px",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
      boxShadow: "0 40px 100px rgba(0, 0, 0, 0.3)",
      overflow: "hidden",
      animation: "scaleIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    modalHeader: {
      padding: "20px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
    },
    modalHeaderLeft: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
    },
    modalIcon: {
      width: "50px",
      height: "50px",
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      fontSize: "24px",
      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
    },
    modalTitle: {
      margin: 0,
      fontSize: "18px",
      fontWeight: "900",
      color: isDark ? "#f8fafc" : "#0e1829",
    },
    modalSub: {
      margin: "4px 0 0 0",
      fontSize: "13px",
      fontWeight: "800",
      color: isDark ? "#cbd5e1" : "#586274",
    },
    modalClose: {
      width: "36px",
      height: "36px",
      border: "1px solid rgba(15, 23, 42, 0.1)",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255, 255, 255, 0.8)",
      color: isDark ? "#f8fafc" : "#0e1829",
      borderRadius: "12px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "18px",
      transition: "all 0.3s ease",
    },
    modalMeta: {
      padding: "16px 24px",
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
    },
    metaPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 14px",
      borderRadius: "999px",
      border: "1px solid rgba(102, 126, 234, 0.15)",
      background: "rgba(102, 126, 234, 0.08)",
      fontSize: "12px",
      fontWeight: "900",
      color: isDark ? "#f8fafc" : "#0e1829",
    },
    metaLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 14px",
      borderRadius: "999px",
      border: "1px solid rgba(102, 126, 234, 0.15)",
      background: "rgba(102, 126, 234, 0.08)",
      fontSize: "12px",
      fontWeight: "900",
      color: "#667eea",
      textDecoration: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    modalBody: {
      padding: "24px",
      maxHeight: "50vh",
      overflowY: "auto",
    },
    modalMessage: {
      margin: 0,
      whiteSpace: "pre-wrap",
      lineHeight: 1.7,
      color: isDark ? "#f8fafc" : "#0e1829",
      fontWeight: "600",
      fontSize: "15px",
    },
    modalActions: {
      padding: "20px 24px",
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(230, 236, 255, 0.9)",
    },
    modalBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      border: "1px solid rgba(15, 23, 42, 0.12)",
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15, 23, 42, 0.05)",
      color: isDark ? "#f8fafc" : "#0e1829",
      borderRadius: "14px",
      padding: "12px 20px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "14px",
      transition: "all 0.3s ease",
    },
    modalBtnPrimary: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      border: "none",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      borderRadius: "14px",
      padding: "12px 20px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "14px",
      textDecoration: "none",
      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
      transition: "all 0.3s ease",
    },
  };
}

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes float {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(30px, -30px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 1; }
  }

  .loading-dots span {
    width: 8px;
    height: 8px;
    background: #fff;
    border-radius: 50%;
    animation: dotBounce 1.4s infinite ease-in-out both;
  }

  .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
  .loading-dots span:nth-child(2) { animation-delay: -0.16s; }

  @keyframes dotBounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }

  .header-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2) !important;
  }

  .stat-box:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15) !important;
  }

  .search-input:focus {
    border-color: rgba(102, 126, 234, 0.4);
    background: #fff;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }

  .clear-btn:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: scale(1.1);
  }

  .filter-select:hover {
    border-color: rgba(102, 126, 234, 0.3);
  }

  .filter-select:focus {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }

  .export-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(67, 233, 123, 0.4) !important;
  }

  .table-row:hover {
    background: rgba(102, 126, 234, 0.04);
  }

  .view-btn:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.3);
    transform: scale(1.05);
  }

  .email-link:hover {
    color: #5568d3;
    text-decoration: underline;
  }

  .phone-link:hover {
    color: #667eea;
  }

  .role-badge:hover {
    transform: scale(1.05);
  }

  .delete-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 107, 107, 0.4) !important;
  }

  .page-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(102, 126, 234, 0.4) !important;
  }

  .page-num-btn:hover {
    background: rgba(102, 126, 234, 0.08);
    border-color: rgba(102, 126, 234, 0.3);
    transform: translateY(-1px);
  }

  .modal-close:hover {
    background: rgba(255, 99, 99, 0.1);
    color: #ff6b6b;
    transform: scale(1.1);
  }

  .meta-link:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.3);
  }

  .modal-btn:hover {
    background: rgba(15, 23, 42, 0.1);
    transform: translateY(-1px);
  }

  .modal-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important;
  }

  ::-webkit-scrollbar {
    height: 12px;
    width: 12px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(230, 236, 255, 0.8);
    border-radius: 999px;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.6), rgba(118, 75, 162, 0.6));
    border-radius: 999px;
    border: 2px solid rgba(230, 236, 255, 0.9);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.8), rgba(118, 75, 162, 0.8));
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(102, 126, 234, 0.7) rgba(230, 236, 255, 0.9);
  }
`;

if (!document.getElementById("admin-soumissions-styles")) {
  styleSheet.id = "admin-soumissions-styles";
  document.head.appendChild(styleSheet);
}

export default AdminSoumissions;